var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * @file 按钮组件
 */
import { omit } from '@seiue/util';
import ButtonAntd from 'antd/es/button';
import classNames from 'classnames';
import React, { useState, useEffect, useRef, forwardRef, } from 'react';
import styled, { css } from 'styled-components';
import { Icon } from '../Icon';
import { TooltipWrapper } from '../Tooltip';
import { dangerButtonStyle, darkButtonStyle, defaultButtonStyle, grayButtonStyle, lightTextButtonStyle, linkButtonStyle, primaryButtonStyle, reversedButtonStyle, specialButtonStyle, textButtonStyle, } from './styles';
const iconSizeMap = {
    large: 16,
    middle: 14,
    small: 14,
    xs: 13,
};
export const Button = forwardRef((_a, ref) => {
    var { onClick, loading, loadingText, type = 'default', underline, iconSize, tooltip } = _a, props = __rest(_a, ["onClick", "loading", "loadingText", "type", "underline", "iconSize", "tooltip"]);
    const [executingOnClick, setExecutingOnClick] = useState(false);
    const defaultThrottle = React.useRef(false);
    // 如果显示指定了 loading 就不再关心 executingOnClick
    const isLoading = loading !== null && loading !== void 0 ? loading : executingOnClick;
    /*
     * 以此来阻止在 asyncOnClick 执行过程中发生了页面跳转，使得 ButtonRaw 被 unmount 后，再执行 setExecutingOnClick 引发错误
     * 该错误可能引发内存泄漏
     * 该用法可能是一种常见的该问题的解决方案
     */
    const mounted = useRef(false);
    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    });
    let callback;
    if (onClick) {
        callback = async (e) => {
            if (defaultThrottle.current)
                return;
            defaultThrottle.current = true;
            const throttleTimer = setTimeout(() => {
                defaultThrottle.current = false;
                clearTimeout(throttleTimer);
            }, 225);
            /*
             * 在按钮被触发后 200ms 后且方法尚未执行完毕时，将按钮设置为 loading 状态
             * 为了瞬间完成的按钮不闪一个 loading
             */
            const timer = setTimeout(() => {
                if (mounted.current) {
                    setExecutingOnClick(true);
                }
            }, 200);
            try {
                await onClick(e);
            }
            finally {
                clearTimeout(timer);
                if (mounted.current)
                    setExecutingOnClick(false);
            }
        };
    }
    const className = classNames(props.className, {
        [`se-button__${type}`]: !!type,
    });
    const antdBtnType = (['ghost', 'link', 'default', 'primary', 'dashed', undefined].includes(type)
        ? type
        : undefined);
    return (_jsx(ButtonTooltip, Object.assign({ className: "se-button__tooltip", tooltip: tooltip, "$fontWeight": props.fontWeight }, { children: _jsxs(ButtonStyled, Object.assign({ loading: isLoading, onClick: callback, type: antdBtnType }, omit(props, 'type', 'icon', 'iconRight', 'size', 'danger'), { danger: type === 'danger', 
            // xs 是在 small 基础上再微调
            size: props.size === 'xs' ? 'small' : props.size, 
            // https://styled-components.com/docs/faqs#why-am-i-getting-html-attribute-warnings
            xs: props.size === 'xs' ? 1 : 0, "$underline": underline, className: className, ref: ref }, { children: [props.icon ? (_jsx(Icon, { icon: props.icon, size: iconSize !== null && iconSize !== void 0 ? iconSize : (props.size && iconSizeMap[props.size]), onClick: callback }, void 0)) : null, isLoading && loadingText ? loadingText : props.children, props.iconRight ? (_jsx(Icon, { icon: props.iconRight, size: iconSize !== null && iconSize !== void 0 ? iconSize : (props.size && iconSizeMap[props.size]), onClick: callback }, void 0)) : null] }), void 0) }), void 0));
});
// @ts-expect-error it's ok
Button.__ANT_BUTTON = true; // 令其他 antd 组件可以识别该按钮为 antButton，比如 tooltip，相关 issue：https://github.com/ant-design/ant-design/issues/18137
const ButtonStyled = styled(ButtonAntd) `
  & + &,
  & + .ant-btn-group,
  & + .se-button__tooltip .ant-btn,
  .ant-btn-group + & {
    margin-left: 12px;
  }

  &.ant-btn-sm {
    padding-top: 0;
    padding-bottom: 0;
  }

  &.se-button__default {
    ${defaultButtonStyle};
  }

  &.se-button__primary {
    ${primaryButtonStyle};
  }

  &.se-button__danger {
    ${dangerButtonStyle};
  }

  &.se-button__link {
    ${linkButtonStyle};
  }

  &.se-button__special {
    ${specialButtonStyle};
  }

  &.se-button__gray {
    ${grayButtonStyle};
  }

  &.se-button__reversed {
    ${reversedButtonStyle};
  }

  &.se-button__text {
    ${textButtonStyle};
  }

  &.se-button__light-text {
    ${lightTextButtonStyle};
  }

  &.se-button__dark {
    ${darkButtonStyle};
  }

  &&& {
    ${props => props.fontWeight
    ? css `
            font-weight: ${props.fontWeight};
          `
    : ''};

    ${props => props.xs
    ? css `
            height: 24px;
            padding-top: 0;
            padding-bottom: 0;
            font-size: 13px;
            line-height: 23px;
          `
    : ''};

    & > .anticon + span,
    & > span + .anticon {
      margin-left: 6px;
    }

    ${p => p.$underline &&
    css `
        span {
          text-decoration: underline;
        }
      `};
  }
`;
/**
 * ant design Button[disabled] 与 Tooltip 搭配使用时，会导致 DOM 结构变化，
 * 原本属于 Button 的 class 被移到了 Tooltip，具体原因请见官方答复：
 * https://github.com/ant-design/ant-design/issues/21404#issuecomment-586800984
 */
const ButtonTooltip = styled(TooltipWrapper) `
  &.se-button__default .ant-btn {
    ${defaultButtonStyle};
  }

  &.se-button__primary .ant-btn {
    ${primaryButtonStyle};
  }

  &.se-button__danger .ant-btn {
    ${dangerButtonStyle};
  }

  &.se-button__link .ant-btn {
    ${linkButtonStyle};
  }

  &.se-button__special .ant-btn {
    ${specialButtonStyle};
  }

  &.se-button__gray .ant-btn {
    ${grayButtonStyle};
  }

  &.se-button__reversed .ant-btn {
    ${reversedButtonStyle};
  }

  &.se-button__text .ant-btn {
    ${textButtonStyle};
  }

  &.se-button__light-text .ant-btn {
    ${lightTextButtonStyle};
  }

  &.se-button__dark .ant-btn {
    ${darkButtonStyle};
  }
`;
//# sourceMappingURL=data:application/json;base64,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